/**
 * @namespace category-price-ceiling.store
 * @description A small module-level cache of top-level category price ceilings,
 * keyed by the category's breadcrumb (e.g. "Products>Computers and Portables").
 *
 * The ceiling is authored per top-level (Level 1) category in the STEP category
 * feed (attribute EXX-SabrePC-PriceCeiling) and synced onto Category documents.
 * It is loaded once at app initialization (SPC only) via CategoryService and
 * read synchronously by WebstoreProduct when computing its pricing/button action:
 * a product whose price is >= its category ceiling shows "Send Inquiry" instead
 * of "Add to Cart".
 *
 * This mirrors the module-level `environment` pattern used in
 * webstore-product.class.ts so the synchronous pricing computation can access
 * the ceiling without threading it through every WebstoreProduct construction
 * site.
 */

// eslint-disable-next-line no-restricted-syntax -- site-wide data; every request sets the same map
let ceilings: Record<string, number> = {};

/**
 * @function setCategoryPriceCeilings
 * @param {Record<string, number>} map - breadcrumb -> price ceiling
 * @description Replaces the cached ceilings map. Called once at app init.
 */
export function setCategoryPriceCeilings(map: Record<string, number>): void {
    ceilings = map || {};
}

/**
 * @function getCategoryPriceCeiling
 * @param {string} breadcrumb - The category breadcrumb key (e.g. "Products>Computers and Portables")
 * @returns {number} The ceiling for the category, or 0 if none is set.
 */
export function getCategoryPriceCeiling(breadcrumb: string): number {
    return (breadcrumb && ceilings[breadcrumb]) || 0;
}
